import { RowData } from '@common/interims/types';
import { ConcurrencyPolicy } from '@common/sqlIde/types/SensorSchedule';
import { DependencyType } from '@common/types/dependencyTypes';
import { LinkType, ProductType, ShareScopeType } from '@prophecy/graphql-types/enums';
import { ObjectLiteral } from '@prophecy/interfaces/generic';

import { Session } from '../../common/types/sessionTypes';
import { SQLType } from '../../schema/sqlTypes';

export type ControlCenterResponse = {
  data: {
    key: string;
    url?: string;
  };
};

export type ProphecyManagedData = {
  orgID: string;
  orgWideTeamName: string;
  scimToken: string;
  type: AuthTypes;
  idp?: ProviderType;
  usernamePrompt?: string;
  idpconfig?: unknown;
};

export enum SCIMVersion {
  v0 = 'v0',
  v1 = 'v1'
}

export type UsgaeMetricsResponse = {
  data: {
    aggregateMetrics: ObjectLiteral;
    requestID: string;
  };
};
export type AdminConfigType = { configType: string; value: object };
export enum ExecutionStatus {
  INITIALIZED = 'INITIALIZED', // this is an FE status doesnt exist in BE
  NOT_RUNNING = 'NOT_RUNNING',
  RUNNING = 'RUNNING',
  SUCCEEDED = 'SUCCEEDED',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  FAILED = 'FAILED',
  TIMEDOUT = 'TIMEDOUT',
  CANCELLED = 'CANCELLED',
  SKIPPED = 'SKIPPED',
  PENDING = 'PENDING',
  TERMINATING = 'TERMINATING',
  TERMINATED = 'TERMINATED',
  BLOCKED = 'BLOCKED',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  UNKNOWN = 'UNKNOWN',
  UPSTREAM_SKIPPED = 'UPSTREAM_SKIPPED',
  NOT_AVAILABLE = 'NOT_AVAILABLE'
}

export type ExecutionLog = {
  message: string;
  level: string;
  trace?: string;
  errorCode?: string;
};

export enum AuthTypes {
  prophecymanaged = 'ProphecyManaged',
  ldap = 'ldap',
  saml = 'saml',
  azuread = 'azuread',
  google = 'google'
}

export enum ProviderType {
  google = 'google',
  okta = 'okta',
  azuread = 'azuread',
  others = 'others'
}

export type DatabrickUserPasswordPayload = {
  identifier: string;
  password: string;
  sendpromotionalemail: boolean;
  teamName?: string;
  firstName: string;
  lastName?: string;
  teamUID?: string;
  token?: string;
  email?: string;
  company?: string;
  teamNameUpdationAllowed?: string;
};

export enum ClusterActionType {
  restart = 'Restart',
  start = 'Start',
  terminate = 'Terminate',
  fixDependencies = 'FixDependencies',
  connectAnyway = 'ConnectAnyway'
}

export type LibraryOperation = 'Install' | 'Uninstall';

export type ClusterDatabricksOpsResponse = {
  libResults?: {
    status: 'Failure' | 'Success';
    message: string;
    installSummary: {
      total: number;
      successful: number;
      failed: number;
    };
    uninstallSummary: {
      total: number;
      successful: number;
      failed: number;
    };
    libraries: {
      name: string;
      operation: LibraryOperation;
      status: 'Failure' | 'Success';
      error: string;
    }[];
  };
  allowConnectAnyways: boolean;
  allowWebsocketCall: boolean;
  displayMessage: string;
};

export type ClusterDatabricksOpsPayload = {
  type: ClusterActionType;
  clusterId: string;
  libsToUninstall?: { dependencies: DependencyType[] };
  clusterData?: Session;
};

export enum KGProgressStatus {
  InProgress = 'IN_PROGRESS',
  Processing = 'PROCESSING',
  Error = 'ERROR',
  Success = 'SUCCESS',
  Pending = 'PENDING',
  Failed = 'FAILED',
  Idle = 'IDLE',
  Cancelled = 'CANCELLED'
}

export type KGConnectionStatus = {
  diagnostics?: string;
  total_databases: number;
  completed_databases: number;
  total_schemas: number;
  completed_schemas: number;
  total_tables: number;
  completed_tables: number;
  status: KGProgressStatus;
  completed_files: number;
  total_root_directories: number;
  completed_root_directories: number;
  last_completed_schema: string;
  last_completed_object: string;
};

export type KGCrawlStatusResponse = {
  session_id?: string;
  status?: KGProgressStatus;
  start_time?: number;
  last_run?: number;
  last_successful_run?: number;
  progress?: {
    total_connections: number;
    completed_connections: number;
    total_databases: number;
    completed_databases: number;
    total_schemas: number;
    completed_schemas: number;
    completed_files: number;
    connections: {
      [connectionName: string]: KGConnectionStatus;
    };
  };
};

// Flattened root status with progress metrics at top level (for hook return when no connectionName provided)
export type FlattenedRootStatus = {
  session_id?: string;
  status?: KGProgressStatus;
  start_time?: number;
  last_run?: number;
  last_successful_run?: number;
  // Flattened progress metrics
  total_connections?: number;
  completed_connections?: number;
  total_databases?: number;
  completed_databases?: number;
  total_schemas?: number;
  completed_schemas?: number;
  completed_files?: number;
  diagnostics?: { [connectionName: string]: string | undefined };
};

export enum LastTriggerStatus {
  Ran = 'ran',
  Skipped = 'skipped'
}

export type OrchestrationScheduledPipelineResponse = {
  pipelines?: {
    cron: string;
    projectId: string;
    pipelineName: string;
    fabricId: string;
    scheduleType: string;
    // the deployed value on this fabric; can lag the branch between publishes
    concurrencyPolicy: ConcurrencyPolicy;
    // describe the last slot, not the last run - both absent together for a schedule that has never fired
    lastTriggerStatus?: LastTriggerStatus;
    lastTriggerTime?: string;
    runs: {
      id: string;
      runStatus: string;
      createdAt: string;
      errorMessage?: string;
      sensorConfigDetails: string;
    }[];
  }[];
};

export { ScheduleDecisionAction, ScheduleImpactStatus } from '@prophecy/shared-core/schedule';
export type {
  ScheduleDecision,
  ScheduleImpactRequest,
  ScheduleImpactResponse,
  ScheduleImpactRow,
  ScheduleTriggerType
} from '@prophecy/shared-core/schedule';

export enum SqlPipelineRunType {
  Scheduled = 'Scheduled',
  InteractivePipelineRun = 'InteractivePipelineRun',
  InteractiveAppRun = 'InteractiveAppRun',
  AppSchedule = 'AppSchedule',
  APIPipelineRun = 'APIPipelineRun'
}

export enum OrchestrationPipelineStatus {
  Cancelling = 'cancelling',
  AlreadyFinished = 'already_finished',
  Cancelled = 'cancelled'
}

export enum OrchestrationPipelineRunStatus {
  CANCELLING = 'CANCELLING' /* cancel accepted but still running */,
  CANCELLED = 'CANCELLED',
  RUNNING = 'RUNNING',
  SUCCEEDED = 'SUCCEEDED',
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  QUEUED = 'QUEUED' /* cron slot waiting for the schedule's previous run */,
  SKIPPED = 'SKIPPED' /* cron slot dropped by the schedule's concurrency policy */
}

export enum TriggerSourceEnum {
  USER = 'USER',
  SCHEDULE = 'SCHEDULE',
  API = 'API',
  CLI = 'CLI',
  PARENT_RUN = 'PARENT_RUN'
}

export enum CancelSourceEnum {
  USER = 'USER',
  SCHEDULE_POLICY = 'SCHEDULE_POLICY',
  PARENT_RUN = 'PARENT_RUN'
}

export type RunAttributionType<TSource extends string> = {
  // a newer orchestrator may add sources; those render as "Unknown" rather than failing
  source: TSource | (string & {});
  userId?: string;
  email?: string;
  at?: string;
};

export type TriggerAttribution = RunAttributionType<TriggerSourceEnum>;
export type CancelAttribution = RunAttributionType<CancelSourceEnum>;

/**
 * 202	cancelling	Live run, or a repeat call	Row -> CANCELLING, start polling
200	already_finished	Row already terminal; runStatus is the final status	Toast "Run already finished", refetch
200	cancelled	Orphan row closed out	Row -> CANCELLED, refetch
404	n/a	Unknown runId	Toast "Run not found", refetch
403	n/a	Run's fabric not in token, or read-only replay/share token	Toast "No permission to stop this run"
400	n/a	Invalid runId	Generic error toast (should not happen from the UI)
 */

export type OrchestrationPipelineRunResponse = {
  history?: {
    projectId: string;
    pipelineName: string;
    startTime: string;
    runId: string;
    endTime?: string;
    errorMessage?: string;
    runType: SqlPipelineRunType;
    fabricId: string;
    duration_ns: number;
    appName?: string;
    instanceName?: string;
    cron: string;
    sensorConfigDetails: string;
    hasRuntimeLogs?: boolean;
    runStatus: OrchestrationPipelineRunStatus;
    triggeredBy?: TriggerAttribution;
    cancelledBy?: CancelAttribution;
    /** Children recorded before trigger tracking may hold a legacy numeric id that matches no row. */
    parentRunId?: string;
    rerunOfRunId?: string;
    /** Set on a restart attempt: the runId of the attempt it replaced. */
    restartOfRunId?: string;
    /** Set on a restart attempt: the runId of the first attempt in its restart chain. */
    rootRunId?: string;
    /** Sent embedded in each run; every field is blank unless the fabric still exists. */
    fabric?: {
      ID: string;
      FabricID: string;
      TeamID: string;
      Name: string;
      FabricType: string;
      Properties: unknown;
      CreatedAt: string;
      UpdatedAt: string;
    };
  }[];
  totalRows?: number;
};

/** Every field is optional: an absent one uses the schedule's current configuration. */
export type OrchestrationPipelineRunRestartRequest = {
  /** Optimistic-concurrency guard: the restart fails if this is no longer the latest attempt. */
  latestRunId?: string;
  pipelineParameterSet?: string;
  pipelineParameters?: Record<string, string>;
  projectParameterSet?: string;
  projectParameters?: Record<string, string>;
};

export type OrchestrationPipelineRunRestartResponse = {
  runId: string;
  restartOfRunId: string;
  rootRunId: string;
  attempt: number;
  releaseVersion: string;
  runStatus: OrchestrationPipelineRunStatus;
};

export type OrchestrationPipelineRunCancelResponse = {
  runId: string;
  status: OrchestrationPipelineStatus;
  runStatus: OrchestrationPipelineRunStatus;
  descendantsCancelled: number;
  message: string;
};

export type SchemaParams = {
  port: string;
  processId: string;
  processLabel: string;
  schema: SQLType;
};

export type LazyInterimParams = {
  port: string;
  processId: string;
};

export type CountParams = {
  count: number;
  dataTruncated: number;
  port: string;
  processId: string;
  processLabel: string;
};

export type PipelineRunParams = {
  content: string;
  scope?: ('logs' | 'toast')[];
  // Bulk `gemProgress` hoists the shared fields here (including a shared `exception`) and sends one entry
  // per gem in `progress`; the legacy single-gem shape keeps them inside the `progress` object.
  task_state?: number;
  start_time?: number;
  end_time?: number;
  exception?: string;
  progress?:
    | {
        process_id: string;
        start_time: number;
        task_state: number;
        end_time?: number;
        exception?: string;
        gem_name?: string;
      }
    | { process_id: string; gem_name?: string }[];
};

export type OrchestrationPipelineRunLogsResponse<UiPipelineGraph = unknown, Diagnostics = unknown> = {
  id: number;
  created_at: string;
  project_id: string;
  user_id: string;
  run_id: string;
  run_request_params: {
    historicRunId?: string;
    pipelineState?: {
      currentPipelineGraph: UiPipelineGraph;
      diagnostics: Diagnostics;
    };
  } & Record<string, unknown>;
  messages: {
    messageNumber: number;
    time: string;
    message: {
      method: string;
      params: SchemaParams | LazyInterimParams | CountParams | PipelineRunParams;
    };
  }[];
  // Terminal run error (persisted live JSON-RPC `error` frame); `run_error.error` feeds `extractRunErrors`. Absent on success.
  run_error?: {
    error?: {
      code?: number;
      message?: string;
      data?: unknown;
      envelope?: unknown;
    };
  };
};

export type OrchestrationPipelineRunInterimSamplesLogsResponse = {
  run_id: string;
  process_id: string;
  port: string;
  row_count: number;
  created_at: string;
  data: {
    method: 'samples';
    params: {
      port: string;
      processId: string;
      processLabel: string;
      data: RowData[];
      dataTruncated: boolean;
    };
  };
};

export type ReplayDetails = {
  replayDetails: {
    uid: string;
    name: string;
    description: string;
    replayId: string;
    sharedUserUID: string;
    projectUID: string;
    fabricUID: string;
    pipelineName: string;
    appNames: string[];
    replayJson: string;
    shareLink: string;
    createdAt: string;
  };
  jwtToken?: string;
  hasDirectAccess: boolean;
  loggedInUser?: string;
};
export type ShareDetails = {
  shareDetails: {
    uid: string;
    shareId: string;
    sharedUserUID: string;
    projectUID: string;
    fabricUID: string;
    shareScope: ShareScopeType;
    entityURI?: string;
    linkType?: LinkType;
  };
  jwtToken?: string;
  hasDirectAccess: boolean;
  loggedInUser?: string;
};
export type BotCheckResponse = {
  isBot: boolean;
  score: number;
};

export type FeatureSchedulerPayload = {
  featureID: string;
  action: 'enabled' | 'disabled';
  selectedOption?: string;
}[];

export type OAuthGoogleAuthenticatePayload = {
  identifier?: string;
  productType: ProductType | undefined;
};
